import React, { memo, PropsWithChildren, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { logger } from '@garnish/logger';
import { PortalProvider } from '@gorhom/portal';

import { theme } from '@sg/garnish';

import { useAwsWafScript } from '@order/integrations/aws-waf';
import { UsableNetBanner } from '@order/usable-net';

import {
  OtaUpdateDialog,
  useResetDietaryRestrictionsOnLogout,
  useResetLoyaltyOffersOnLogout,
} from './components';
import { useValidOrders } from './context';
import { useCustomerTelemetry } from './context/Customer';
import { useInitializeDietaryPreferences } from './context/DietaryPreferences';
import { useTrackLastInteractedStore } from './context/LastInteractedStore';
import { useResetPlacedOrdersCounterOnLogout } from './context/PlacedOrdersCounter';
import {
  useAccessibilityTracking,
  useAppTrackingTransparency,
  useCancelLiveActivitiesOnLogout,
  useFetchUserCart,
  useHasUnseenChallenges,
  useLocationPermissionTracking,
  useLoyaltyPremiumStatus,
  useNetworkSubscription,
  useOrderStatusNotificationPreferences,
  usePushNotifications,
  useRadarForegroundTracking,
  useRegisterUrqlErrorTracker,
  useRegisterUrqlLogOutHandler,
  useResetSierraOnLogout,
} from './hooks';
import { useFacebookPixel } from './integrations/facebook-pixel';
import { useLiveActivityTelemetry } from './integrations/live-activities';

// ────────────────────────────────────────────────────────────────────────────────

export const AppContent = memo((props: PropsWithChildren) => {
  const { children } = props;

  useCustomerTelemetry();
  useFetchUserCart();
  useNetworkSubscription();
  usePushNotifications();
  useAppTrackingTransparency();
  useFacebookPixel();
  useTrackLastInteractedStore();
  useAccessibilityTracking();
  useLocationPermissionTracking();
  useRadarForegroundTracking();
  useRegisterUrqlErrorTracker();
  useRegisterUrqlLogOutHandler();
  useLiveActivityTelemetry();
  useValidOrders('network-only');
  useAwsWafScript();
  useInitializeDietaryPreferences();

  // ─── App Load Graphql Updates ────────────────────────────────────────

  useLoyaltyPremiumStatus({ requestPolicy: 'cache-and-network' });
  useHasUnseenChallenges('cache-and-network');
  useOrderStatusNotificationPreferences({ requestPolicy: 'cache-and-network' });

  // ─── Logout Resets ───────────────────────────────────────────────────

  useResetDietaryRestrictionsOnLogout();
  useResetPlacedOrdersCounterOnLogout();
  useResetSierraOnLogout();
  useResetLoyaltyOffersOnLogout();
  useCancelLiveActivitiesOnLogout();

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    logger.debug('App content render');
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View testID="app.container" style={styles.appContainer}>
      <UsableNetBanner />

      <PortalProvider>
        <OtaUpdateDialog />
        {children}
      </PortalProvider>
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  appContainer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
});
